import { App as AntdApp, Drawer, Descriptions, Typography, Space, Tag, Button } from 'antd'
import {
  canEditKnowledge,
  canManageKnowledge,
  knowledgeProductDisplay,
  KNOWLEDGE_KIND_COLOR,
  KNOWLEDGE_PROBLEM_TYPE_COLOR,
  KNOWLEDGE_SOURCE_TYPE_COLOR,
  KNOWLEDGE_STATUS_COLOR,
} from '@/modules/knowledge/constants'
import { usePublishKnowledge, useArchiveKnowledge, useRegeneratePublicVersion } from '@/modules/knowledge/queries'
import { useAuthStore } from '@/stores/authStore'
import type { KnowledgeEntry } from '@/types/knowledge'

const { Text, Title, Paragraph, Link } = Typography

// 对外版本的状态配色：缺失/过期是"不能发布"的状态，用醒目色
const PUBLIC_STATE_COLOR: Record<string, string> = {
  fresh: 'green',
  stale: 'orange',
  missing: 'red',
}

interface Props {
  // 列表/检索结果本身就是完整条目，详情直接渲染，避免再发一次只有旧字段的详情请求
  entry: KnowledgeEntry | null
  onClose: () => void
  onEdit: (entry: KnowledgeEntry) => void
}

export default function KnowledgeDetailDrawer({ entry, onClose, onEdit }: Props) {
  const { message, modal } = AntdApp.useApp()
  const publishMutation = usePublishKnowledge()
  const archiveMutation = useArchiveKnowledge()
  const publicMutation = useRegeneratePublicVersion()
  const roles = useAuthStore((s) => s.roles)
  const canManage = canManageKnowledge(roles)
  const canEdit = canEditKnowledge(roles)

  // 一次点击确认，不再弹输入框让审核者写理由。
  // 业务方 2026-09-17："审核能不能不弹出加理由了，影响审核速度。"
  // 实测 14 次发布填的全是"已核对"（零信息量），说明必填只是拖慢流程；
  // 现在由后端自动把事实写进审核流水（条数 / 其中 AI 条数 / 操作人 / 时间）。
  const confirmPublish = () => {
    if (!entry) return
    modal.confirm({
      title: '发布这条知识？',
      content: entry.ai_generated
        ? '这条是 AI 二次梳理的产物，发布后进入客服问答库（发出去的是脱敏后的对外版本）。'
        : '发布后该条目会进入客服问答库。',
      okText: '发布',
      cancelText: '取消',
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          publishMutation.mutate(
            { entryId: entry.id },
            {
              onSuccess: () => {
                message.success('已发布')
                resolve()
              },
              onError: (err) => {
                message.error(
                  err instanceof Error ? err.message : '发布失败，当前角色可能无此权限',
                )
                reject(err)
              },
            },
          )
        }),
    })
  }

  const confirmArchive = () => {
    if (!entry) return
    const isPublished = entry.status === 'published'
    modal.confirm({
      title: isPublished ? '下架这条知识？' : '退回这条知识？',
      content: isPublished
        ? '下架后该条目会从客服问答库移除。'
        : '退回后该条目不会进入客服问答库。',
      okText: isPublished ? '确定下架' : '确定退回',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          archiveMutation.mutate(
            { entryId: entry.id },
            {
              onSuccess: () => {
                message.success(isPublished ? '已下架' : '已退回')
                resolve()
              },
              onError: (err) => {
                message.error(
                  err instanceof Error ? err.message : '操作失败，当前角色可能无此权限',
                )
                reject(err)
              },
            },
          )
        }),
    })
  }

  const isLoadingAction = publishMutation.isPending || archiveMutation.isPending

  return (
    <Drawer
      title="知识条目详情"
      size="large"
      open={!!entry}
      onClose={onClose}
      destroyOnHidden
      extra={
        entry ? (
          <Space>
            {canEdit && (
              <Button size="small" onClick={() => onEdit(entry)}>
                编辑
              </Button>
            )}
            {canManage && entry.status === 'draft' && (
              <Button type="primary" size="small" loading={isLoadingAction} onClick={confirmPublish}>
                发布
              </Button>
            )}
            {canManage && entry.status !== 'archived' && (
              <Button size="small" loading={isLoadingAction} danger onClick={confirmArchive}>
                {entry.status === 'published' ? '下架' : '退回'}
              </Button>
            )}
          </Space>
        ) : null
      }
    >
      {entry ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="条目ID">{entry.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={KNOWLEDGE_STATUS_COLOR[entry.status]}>{entry.status_label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="标题" span={2}>
              {entry.title}
            </Descriptions.Item>
            <Descriptions.Item label="知识形态">
              <Tag color={KNOWLEDGE_KIND_COLOR[entry.kind]}>{entry.kind_label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="问题类型">
              {entry.problem_type_label ? (
                <Tag color={KNOWLEDGE_PROBLEM_TYPE_COLOR[entry.problem_type]}>
                  {entry.problem_type_label}
                </Tag>
              ) : (
                <Text type="secondary">未分类</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="产品">
              {knowledgeProductDisplay(entry.product_key, entry.product_name, entry.product_line)}
            </Descriptions.Item>
            <Descriptions.Item label="产品线">
              {entry.product_line || <Text type="secondary">-</Text>}
            </Descriptions.Item>
            {/* 工程型号属于二级信息：产品主分类只看彩页 product_key */}
            {(entry.zentao_product_id !== 0 || entry.model_names.length > 0) && (
              <Descriptions.Item label="工程型号（二级信息）" span={2}>
                <Space size={4} wrap>
                  {entry.zentao_product_id !== 0 && (
                    <Tag color="default">禅道产品 #{entry.zentao_product_id}</Tag>
                  )}
                  {entry.model_names.map((model) => (
                    <Tag key={model} color="cyan">{model}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="来源类型">
              <Tag color={KNOWLEDGE_SOURCE_TYPE_COLOR[entry.source_type]}>
                {entry.source_type_label}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="来源工单">
              {entry.source_ticket_id ? `#${entry.source_ticket_id}` : <Text type="secondary">-</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="来源标识">
              {entry.source_ref || <Text type="secondary">-</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="来源链接">
              {entry.source_url ? (
                <Link href={entry.source_url} target="_blank" rel="noreferrer">
                  {entry.source_url}
                </Link>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="向量索引" span={2}>
              {entry.is_indexed ? (
                <Tag color="green">已建立向量索引</Tag>
              ) : (
                <Tag color="orange">待建立向量索引</Tag>
              )}
              {entry.index_error && (
                <Text type="danger" style={{ marginInlineStart: 8 }}>
                  {entry.index_error}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="作者">{entry.author}</Descriptions.Item>
            <Descriptions.Item label="审核人">{entry.reviewer || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{entry.created_at}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{entry.updated_at}</Descriptions.Item>
            {entry.published_at && (
              <Descriptions.Item label="发布时间" span={2}>
                {entry.published_at}
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* 关键词 */}
          {entry.keywords && entry.keywords.length > 0 && (
            <div>
              <Text strong>关键词</Text>
              <div style={{ marginTop: 8 }}>
                {entry.keywords.map((kw, i) => (
                  <Tag key={i} color="blue">{kw}</Tag>
                ))}
              </div>
            </div>
          )}

          {/* 正文内容：保留换行，超长可滚动 */}
          <div>
            <Title level={5}>正文内容（内部版）</Title>
            <Paragraph
              style={{
                marginTop: 8,
                marginBottom: 0,
                background: '#fafafa',
                padding: 12,
                borderRadius: 4,
                whiteSpace: 'pre-wrap',
                maxHeight: 420,
                overflow: 'auto',
              }}
            >
              {entry.content}
            </Paragraph>
          </div>

          {/* 对外版本：客服问答库/MCP 拿到的就是这一份 */}
          <div>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Title level={5} style={{ margin: 0 }}>
                对外版本（脱敏后发客服库）
              </Title>
              <Space>
                <Tag color={PUBLIC_STATE_COLOR[entry.public_state] || 'default'}>
                  {entry.public_state_label || '未知'}
                </Tag>
                {canEdit && (
                  <Button
                    size="small"
                    loading={publicMutation.isPending}
                    onClick={() =>
                      publicMutation.mutate(entry.id, {
                        onSuccess: () => message.success('对外版本已重新生成'),
                        onError: (err) =>
                          message.error(err instanceof Error ? err.message : '生成失败'),
                      })
                    }
                  >
                    重新生成
                  </Button>
                )}
              </Space>
            </Space>
            {entry.public_summary ? (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">已打码：{entry.public_summary}</Text>
              </div>
            ) : (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">这一条没有需要打码的内容。</Text>
              </div>
            )}
            {entry.public_content ? (
              <Paragraph
                style={{
                  marginTop: 8,
                  marginBottom: 0,
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  padding: 12,
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 420,
                  overflow: 'auto',
                }}
              >
                {entry.public_content}
              </Paragraph>
            ) : (
              <Paragraph style={{ marginTop: 8, marginBottom: 0 }} type="warning">
                尚未生成对外版本。**没有对外版本的条目不允许发布** —— 否则会把现场凭据
                直接推给客服库。
              </Paragraph>
            )}
          </div>
        </Space>
      ) : null}
    </Drawer>
  )
}
